import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { consoleApi } from '@/lib/consoleApi';
import {
  REVIEW_STATUS_META, strataLabel, matchesRow, cohortConcordance, catalogueByCategory,
  type PeriReviewQueueRow,
} from '@/lib/periReview';
import { AdminPage, RegistryTable } from '@/admin';

function StatusChip({ status }: { status: PeriReviewQueueRow['review_status'] }) {
  const meta = REVIEW_STATUS_META[status];
  return (
    <span className={`inline-block whitespace-nowrap rounded-pill px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function ModeChip({ mode }: { mode: PeriReviewQueueRow['review_mode'] }) {
  return (
    <span className={`inline-block rounded-pill px-2 py-0.5 text-[11px] font-semibold ${mode === 'blinded' ? 'mod-sign' : 'mod-form'}`}>
      {mode}
    </span>
  );
}

export function PeriReviewPage() {
  const q = useQuery({ queryKey: ['peri-review-queue'], queryFn: () => consoleApi.periReview.queue() });
  const [search, setSearch] = useState('');

  const rows = q.data?.participants ?? [];
  const cohort = q.data?.cohort_status;
  const visible = rows.filter((r) => matchesRow(r, search));
  const concordance = cohortConcordance(rows);
  const catalogue = catalogueByCategory(rows);
  const catalogueCategories = Object.keys(catalogue);

  return (
    <AdminPage
      title="Perimenopause review"
      description="MA closed-cohort review — score each participant's board output against the pre-registered rubric. Blinded participants review the raw inputs first; the board output is withheld until an impression is recorded."
    >
      {cohort ? (
        <div className="mb-13 text-body-sm text-text-secondary">
          <strong className="text-text-primary">{cohort.size}</strong> in cohort
          {' '}({cohort.target.size_min}–{cohort.target.size_max} target) ·{' '}
          <strong className="text-text-primary">{cohort.blinded}</strong> blinded
          {' '}({cohort.target.blinded_min}–{cohort.target.blinded_max}) · {cohort.open} open ·{' '}
          {cohort.review_ready
            ? <span className="text-accent font-semibold">cohort review-ready</span>
            : <span className="text-flagged">cohort not yet review-ready (fill to target)</span>}
        </div>
      ) : null}

      {concordance.scored > 0 ? (
        <div className="mb-13 rounded border border-border bg-surface p-13">
          <div className="text-[12px] font-semibold uppercase text-text-muted mb-1">Primary accuracy metric</div>
          <div className="text-body-sm">
            <strong className="text-text-primary">{concordance.passing}/{concordance.scored}</strong> scored participants pass
            {typeof concordance.pct === 'number' ? <> (<strong>{concordance.pct}%</strong>)</> : null}
            {' '}— no safety issue <em>and</em> substantive concordance on key findings.
          </div>
        </div>
      ) : null}

      <RegistryTable<PeriReviewQueueRow>
        columns={[
          {
            key: 'participant', header: 'Participant', className: 'font-mono text-[12px]',
            render: (r) => (
              <Link className="text-accent hover:underline" to={`/review/peri/${encodeURIComponent(r.participant_ref)}`}>
                {r.participant_ref}
              </Link>
            ),
          },
          { key: 'strata', header: 'Strata', className: 'text-[12px] text-text-secondary', render: (r) => strataLabel(r.strata_stage, r.strata_subbranch) },
          { key: 'mode', header: 'Mode', render: (r) => <ModeChip mode={r.review_mode} /> },
          {
            key: 'headline', header: 'Board headline', className: 'text-[12px]',
            render: (r) => {
              const h = r.headline;
              const parts = [
                h.stance ?? '—',
                h.stage ? `· ${h.stage}` : '',
                typeof h.confidence === 'number' ? `· ${h.confidence}% conf` : '',
              ].filter(Boolean).join(' ');
              return (
                <span>
                  {parts}
                  {h.safety_flag ? <span className="ml-2 text-flagged font-semibold">safety flag</span> : null}
                </span>
              );
            },
          },
          { key: 'status', header: 'Review', render: (r) => <StatusChip status={r.review_status} /> },
        ]}
        rows={visible}
        getRowKey={(r) => r.participant_ref}
        count={rows.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={search ? `No participants match "${search}"` : 'No cohort participants assigned yet.'}
        search={{ value: search, onChange: setSearch, placeholder: 'Search by participant, strata, mode, or status…' }}
      />

      {catalogueCategories.length > 0 ? (
        <section className="mt-21">
          <h3 className="text-body-sm font-semibold uppercase text-text-muted mb-2">
            Disagreement catalogue
          </h3>
          <p className="text-[12px] text-text-secondary mb-3">
            Every non-concordant rubric line across the scored cohort, grouped by category — the backlog feed.
          </p>
          <div className="space-y-3">
            {Object.entries(catalogue).map(([cat, entries]) => (
              <div key={cat} className="rounded border border-border bg-surface p-13">
                <div className="text-body-sm font-semibold mb-2">
                  {cat} <span className="text-text-muted font-normal">· {entries.length}</span>
                </div>
                <ul className="space-y-1">
                  {entries.map((e, i) => (
                    <li key={`${e.participant_ref}-${i}`} className="text-[12px]">
                      <Link className="text-accent hover:underline font-mono" to={`/review/peri/${encodeURIComponent(e.participant_ref)}`}>
                        {e.participant_ref}
                      </Link>
                      {' '}<span className="text-text-secondary">{e.value}</span>
                      {e.note ? <span className="text-text-secondary"> — {e.note}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </AdminPage>
  );
}
